import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Diamond, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function Login() {
  const { finalizeLogin } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("password"); // password | totp
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bootstrapAvailable, setBootstrapAvailable] = useState(false);

  useEffect(() => {
    api.get("/bootstrap/status").then((r) => setBootstrapAvailable(!r.data.bootstrap_completed)).catch(() => {});
  }, []);

  const routeAfterLogin = (data) => {
    if (data.must_change_password) return navigate("/sifre-degistir", { replace: true });
    if (data.must_setup_2fa && data.role === "owner") return navigate("/2fa-kur", { replace: true });
    return navigate(data.role === "cashier" ? "/satis" : "/", { replace: true });
  };

  const onSubmitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: identifier.trim(), password });
      if (data.requires_totp) {
        setMfaToken(data.mfa_token);
        setStep("totp");
        toast.info("Authenticator kodunuzu girin");
      } else {
        finalizeLogin(data);
        toast.success("Hoş geldiniz");
        routeAfterLogin(data);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kullanıcı adı veya şifre hatalı";
      toast.error(typeof msg === "string" ? msg : "Giriş başarısız");
    } finally { setLoading(false); }
  };

  const onSubmitTotp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login-totp", { mfa_token: mfaToken, code: totpCode.trim(), recovery: useRecovery });
      finalizeLogin(data);
      toast.success(data.recovery_used ? "Kurtarma kodu kullanıldı — Authenticator'ı yeniden kurmanız önerilir" : "Hoş geldiniz");
      routeAfterLogin(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Doğrulama başarısız";
      toast.error(typeof msg === "string" ? msg : "Kod hatalı");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-stretch bg-[#faf7f2]">
      <div className="hidden md:flex md:w-1/2 relative" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1585960622850-ed33c41d6418?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBnb2xkJTIwamV3ZWxyeSUyMGJvdXRpcXVlJTIwcmluZyUyMG5lY2tsYWNlfGVufDB8fHx8MTc4NjI2Nzc3MHww&ixlib=rb-4.1.0&q=85)", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-stone-900/70 via-stone-900/40 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Diamond className="w-6 h-6" style={{ color: "#e6c88a" }} />
            <span className="font-mono-code text-xs tracking-[0.35em] text-amber-200/80">RIVACO</span>
          </div>
          <h1 className="font-serif-display text-5xl leading-tight">Butik takı yönetim<br/> ve satış portalı</h1>
          <p className="mt-4 text-stone-200/80 max-w-md">İşletme sahibi, yönetici ve kasiyerler tek platformda.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        {step === "password" && (
          <form onSubmit={onSubmitPassword} className="w-full max-w-md card-lux p-8" data-testid="login-form">
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 mb-4"><Diamond className="w-5 h-5" style={{ color: "#c5a059" }} /><span className="font-mono-code text-xs tracking-[0.35em] text-stone-500">RIVACO</span></div>
              <h2 className="font-serif-display text-3xl text-stone-900">Giriş</h2>
            </div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Kullanıcı Adı veya E-posta</label>
            <input data-testid="login-email" type="text" required value={identifier} onChange={(e) => setIdentifier(e.target.value)} className="w-full mb-4 px-4 py-3 rounded-lg border border-stone-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-100 outline-none bg-white transition" placeholder="kasiyer1 veya e-posta" autoComplete="username" autoFocus />
            <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Şifre</label>
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full mb-6 px-4 py-3 rounded-lg border border-stone-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-100 outline-none bg-white transition" placeholder="••••••••" autoComplete="current-password" />
            <button data-testid="login-submit" type="submit" disabled={loading} className="w-full py-3 rounded-lg gold-gradient text-white font-medium tracking-wide hover:brightness-105 active:scale-[0.99] transition disabled:opacity-60">{loading ? "Giriş yapılıyor..." : "Giriş Yap"}</button>
            <div className="mt-4 text-center">
              <a data-testid="forgot-link" href="/sifremi-unuttum" className="text-xs text-stone-500 hover:text-amber-700">Şifremi unuttum</a>
            </div>
            {bootstrapAvailable && (
              <div className="mt-6 text-center text-xs">
                <span className="text-stone-500">İlk kurulum? </span>
                <a data-testid="bootstrap-link" href="/ilk-kurulum" className="text-amber-700 font-medium hover:underline">Owner hesabını oluştur</a>
              </div>
            )}
          </form>
        )}
        {step === "totp" && (
          <form onSubmit={onSubmitTotp} className="w-full max-w-md card-lux p-8" data-testid="login-totp-form">
            <div className="mb-6 text-center">
              <div className="inline-flex items-center gap-2 mb-2"><ShieldCheck className="w-5 h-5 text-emerald-700" /><span className="font-mono-code text-xs tracking-[0.35em] text-stone-500">İKİNCİ ADIM</span></div>
              <h2 className="font-serif-display text-2xl text-stone-900">Authenticator Kodu</h2>
              <p className="text-xs text-stone-500 mt-2">{useRecovery ? "Kurtarma kodunuzu girin (XXXXX-XXXXX)" : "Authenticator uygulamasındaki 6 haneli kodu girin"}</p>
            </div>
            <input data-testid="login-totp-code" value={totpCode} onChange={(e) => setTotpCode(useRecovery ? e.target.value.toUpperCase().slice(0, 11) : e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder={useRecovery ? "XXXXX-XXXXX" : "123456"} inputMode={useRecovery ? "text" : "numeric"} className="w-full px-4 py-3 rounded-lg border border-stone-200 tracking-[0.4em] text-center text-lg outline-none focus:border-amber-500" autoFocus />
            <button data-testid="login-totp-submit" type="submit" disabled={loading || (useRecovery ? totpCode.length < 10 : totpCode.length !== 6)} className="w-full mt-4 py-3 rounded-lg gold-gradient text-white disabled:opacity-60">{loading ? "Doğrulanıyor..." : "Doğrula ve Gir"}</button>
            <button type="button" data-testid="login-toggle-recovery" onClick={() => { setUseRecovery(!useRecovery); setTotpCode(""); }} className="w-full mt-3 text-xs text-stone-500 hover:text-amber-700">{useRecovery ? "Authenticator kodu kullan" : "Kurtarma kodu kullan"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
